import { startTransition, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ArrowRightLeft,
  ChartColumnIncreasing,
  ChartLine,
  Clock3,
  Globe2,
  Layers3,
  RefreshCw,
  Rows3,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { DatePicker } from '../../components/ui/datePicker';
import HoverHint from '../../components/ui/hoverHint';
import { InputNumber } from '../../components/ui/inputNumber';
import { ShadowBox } from '../../components/ui/shadowbox';
import { cn } from '../../lib/utils';
import {
  buildStatisticsRoute,
  getStatisticsSectionFromPath,
  STATISTICS_SECTIONS,
} from './statistics-modules/config';
import {
  fetchStatisticsFilters,
  fetchStatisticsMarketTrends,
} from './statistics-modules/apiService';

const QUICK_PRESETS = [
  { key: '30d', labelKey: 'app:statistics.quickPresets.30d', defaultLabel: '30D', days: 29, interval: 'day' },
  { key: '90d', labelKey: 'app:statistics.quickPresets.90d', defaultLabel: '90D', days: 89, interval: 'month' },
  { key: '12m', labelKey: 'app:statistics.quickPresets.12m', defaultLabel: '12M', months: -11, interval: 'month' },
  { key: 'all', labelKey: 'app:statistics.quickPresets.all', defaultLabel: 'All time', interval: 'quarter' },
];

const METRIC_OPTIONS = [
  { key: 'amount', labelKey: 'app:statistics.metrics.amount', defaultLabel: 'Capital' },
  { key: 'deals', labelKey: 'app:statistics.metrics.deals', defaultLabel: 'Deals' },
  { key: 'investors', labelKey: 'app:statistics.metrics.investors', defaultLabel: 'Investors' },
];

const GROUP_BY_OPTIONS = [
  { key: 'industry', labelKey: 'app:statistics.groups.industry', defaultLabel: 'Industry' },
  { key: 'country', labelKey: 'app:statistics.groups.country', defaultLabel: 'Country' },
  { key: 'investorType', labelKey: 'app:statistics.groups.investorType', defaultLabel: 'Investor type' },
  { key: 'round', labelKey: 'app:statistics.groups.round', defaultLabel: 'Round' },
];

const INTERVAL_OPTIONS = [
  { key: 'day', labelKey: 'app:statistics.intervals.day', defaultLabel: 'Day' },
  { key: 'month', labelKey: 'app:statistics.intervals.month', defaultLabel: 'Month' },
  { key: 'quarter', labelKey: 'app:statistics.intervals.quarter', defaultLabel: 'Quarter' },
  { key: 'year', labelKey: 'app:statistics.intervals.year', defaultLabel: 'Year' },
];

const INVESTOR_SCOPE_OPTIONS = [
  { key: 'all', labelKey: 'app:statistics.scopes.all', defaultLabel: 'All investors' },
  { key: 'lead', labelKey: 'app:statistics.scopes.lead', defaultLabel: 'Lead only' },
];

const METRIC_CONFIG = {
  amount: {
    labelKey: 'app:statistics.metrics.amountFull',
    defaultLabel: 'Capital deployed',
    dataKey: 'totalAmount',
    color: '#0f766e',
    light: '#99f6e4',
    soft: '#ecfdf5',
  },
  deals: {
    labelKey: 'app:statistics.metrics.dealsFull',
    defaultLabel: 'Deals',
    dataKey: 'dealsCount',
    color: '#1d4ed8',
    light: '#93c5fd',
    soft: '#eff6ff',
  },
  investors: {
    labelKey: 'app:statistics.metrics.investorsFull',
    defaultLabel: 'Active investors',
    dataKey: 'activeInvestors',
    color: '#a16207',
    light: '#fcd34d',
    soft: '#fffbeb',
  },
};

function translateLabel(t, item) {
  return t(item.labelKey, { defaultValue: item.defaultLabel });
}

function getOptionLabel(options, key, t) {
  const option = options.find((candidate) => candidate.key === key);
  return option ? translateLabel(t, option) : key;
}

function getSectionLabel(t, key) {
  return t(`app:statistics.sections.${key}`, { defaultValue: key });
}

function getMetricConfig(t) {
  return Object.fromEntries(
    Object.entries(METRIC_CONFIG).map(([key, value]) => [
      key,
      {
        ...value,
        label: t(value.labelKey, { defaultValue: value.defaultLabel }),
      },
    ])
  );
}

function parseIsoDate(value) {
  if (!value) return null;

  const [year, month, day] = String(value)
    .split('-')
    .map((chunk) => Number.parseInt(chunk, 10));

  if (!year || !month || !day) return null;

  const parsed = new Date(year, month - 1, day);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function formatDateForApi(value) {
  if (!(value instanceof Date) || Number.isNaN(value.getTime())) return null;

  const year = value.getFullYear();
  const month = String(value.getMonth() + 1).padStart(2, '0');
  const day = String(value.getDate()).padStart(2, '0');

  return `${year}-${month}-${day}`;
}

function startOfDay(value) {
  const next = new Date(value);
  next.setHours(0, 0, 0, 0);
  return next;
}

function endOfDay(value) {
  const next = new Date(value);
  next.setHours(23, 59, 59, 999);
  return next;
}

function addDays(value, days) {
  const next = new Date(value);
  next.setDate(next.getDate() + days);
  return next;
}

function addMonths(value, months) {
  const next = new Date(value);
  next.setMonth(next.getMonth() + months);
  return next;
}

function addYears(value, years) {
  const next = new Date(value);
  next.setFullYear(next.getFullYear() + years);
  return next;
}

function clampDate(value, min, max) {
  const time = value.getTime();
  const minTime = min.getTime();
  const maxTime = max.getTime();

  if (time < minTime) return new Date(min);
  if (time > maxTime) return new Date(max);
  return value;
}

function applyPreset(key, meta) {
  const minDate = parseIsoDate(meta?.dates?.min) || addYears(new Date(), -5);
  const maxDate = parseIsoDate(meta?.dates?.max) || new Date();

  if (key === 'all') {
    return {
      datePreset: key,
      dateFrom: startOfDay(minDate),
      dateTo: endOfDay(maxDate),
      interval: 'quarter',
    };
  }

  const preset = QUICK_PRESETS.find((item) => item.key === key) || QUICK_PRESETS[2];
  let dateFrom = startOfDay(maxDate);

  if (typeof preset.days === 'number') {
    dateFrom = startOfDay(addDays(maxDate, -preset.days));
  } else if (typeof preset.months === 'number') {
    dateFrom = startOfDay(addMonths(maxDate, preset.months));
  }

  return {
    datePreset: key,
    dateFrom: clampDate(dateFrom, startOfDay(minDate), endOfDay(maxDate)),
    dateTo: endOfDay(maxDate),
    interval: preset.interval,
  };
}

function createDefaultFilters(meta) {
  const preset = applyPreset('12m', meta);

  return {
    datePreset: preset.datePreset,
    dateFrom: preset.dateFrom,
    dateTo: preset.dateTo,
    industries: [],
    rounds: [],
    investorCountries: [],
    investorTypes: [],
    amountMin: null,
    amountMax: null,
    metric: 'amount',
    groupBy: 'industry',
    interval: preset.interval,
    investorScope: 'all',
  };
}

function compactNumber(value) {
  if (!Number.isFinite(value)) return '0';
  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    maximumFractionDigits: value >= 100 ? 0 : 1,
  }).format(value);
}

function formatCurrency(value) {
  if (!Number.isFinite(value)) return '$0';
  return `$${compactNumber(value)}`;
}

function formatMetricValue(metric, value) {
  if (metric === 'amount') return formatCurrency(value);
  return compactNumber(value);
}

function formatPercent(value) {
  if (!Number.isFinite(value)) return '0%';
  return `${(value * 100).toFixed(value >= 0.1 ? 0 : 1)}%`;
}

function getMetricShare(row, summary, metric) {
  if (metric === 'amount') {
    return summary.totalAmount > 0 ? row.totalAmount / summary.totalAmount : 0;
  }

  if (metric === 'investors') {
    return summary.activeInvestors > 0 ? row.activeInvestors / summary.activeInvestors : 0;
  }

  return summary.dealsCount > 0 ? row.dealsCount / summary.dealsCount : 0;
}

function LoadingOverlay({ text }) {
  const { t } = useTranslation();
  const resolvedText = text || t('app:statistics.loading.generic', { defaultValue: 'Recomputing statistics view...' });

  return (
    <div className="absolute inset-0 z-20 flex items-center justify-center rounded-[inherit] bg-white/72 backdrop-blur-[2px]">
      <div className="flex items-center gap-3 rounded-full border border-stone-200 bg-white px-4 py-2 text-sm text-stone-700 shadow-lg">
        <RefreshCw className="h-4 w-4 animate-spin text-stone-700" />
        <span>{resolvedText}</span>
      </div>
    </div>
  );
}

function SegmentedControl({ label, options, value, onChange }) {
  return (
    <div className="flex h-full flex-col rounded-[20px] border border-stone-200 bg-stone-50/70 px-3 py-2.5 shadow-sm">
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-stone-500">
        {label}
      </div>
      <div className="flex flex-1 flex-wrap content-start gap-1.5">
        {options.map((option) => {
          const active = option.key === value;

          return (
            <button
              key={option.key}
              type="button"
              onClick={() => onChange(option.key)}
              className={cn(
                'cursor-pointer rounded-full border px-2.5 py-1 text-[12px] leading-5 transition-colors',
                active
                  ? 'border-stone-900 bg-stone-900 text-white'
                  : 'border-stone-200 bg-white text-stone-700 hover:border-stone-400'
              )}
            >
              {option.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function MultiSelectFilter({
  label,
  placeholder,
  options,
  values,
  onChange,
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const deferredQuery = useDeferredValue(query);
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const availableOptions = useMemo(() => {
    const selected = new Set(values);
    const normalizedQuery = deferredQuery.trim().toLowerCase();

    return [...(options || [])]
      .sort((left, right) => left.localeCompare(right, undefined, { sensitivity: 'base' }))
      .filter((option) => !selected.has(option))
      .filter((option) => (
        normalizedQuery
          ? option.toLowerCase().includes(normalizedQuery)
          : true
      ));
  }, [deferredQuery, options, values]);

  const handleSelect = (option) => {
    onChange([...values, option]);
    setQuery('');
    setOpen(true);
  };

  const handleRemove = (option) => {
    onChange(values.filter((value) => value !== option));
  };

  return (
    <div className="relative" ref={containerRef}>
      <div className="mb-1 flex items-center justify-between gap-3">
        <label className="text-sm font-medium text-stone-800">{label}</label>
        {values.length > 0 && (
          <button
            type="button"
            onClick={() => onChange([])}
            className="cursor-pointer text-xs text-stone-500 hover:text-stone-900"
          >
            {t('app:statistics.filters.clearSelection', { defaultValue: 'Clear' })}
          </button>
        )}
      </div>

      <div
        className="min-h-[38px] cursor-pointer rounded-[20px] border border-stone-200 bg-stone-50 px-3 py-1.5 shadow-sm"
        onClick={() => setOpen(true)}
      >
        <div className="flex flex-wrap items-center gap-2">
          {values.map((value) => (
            <span
              key={value}
              className="inline-flex items-center gap-2 rounded-full bg-stone-900 px-2.5 py-1 text-xs font-medium text-white"
            >
              <span>{value}</span>
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  handleRemove(value);
                }}
                className="cursor-pointer text-white/80 hover:text-white"
              >
                &times;
              </button>
            </span>
          ))}

          <input
            type="text"
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={(event) => {
              if (event.key === 'Backspace' && !query && values.length > 0) {
                handleRemove(values[values.length - 1]);
              }

              if (event.key === 'Enter' && availableOptions[0]) {
                event.preventDefault();
                handleSelect(availableOptions[0]);
              }
            }}
            placeholder={values.length === 0 ? placeholder : t('app:statistics.filters.addMore', { defaultValue: 'Add more' })}
            className="min-w-[120px] flex-1 cursor-pointer bg-transparent py-1 text-sm outline-none placeholder:text-stone-400"
          />
        </div>
      </div>

      {open && availableOptions.length > 0 && (
        <div className="absolute z-20 mt-2 max-h-64 w-full overflow-y-auto rounded-2xl border border-stone-200 bg-white p-2 shadow-xl">
          {availableOptions.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => handleSelect(option)}
              className="flex w-full cursor-pointer items-center justify-between rounded-xl px-3 py-2 text-left text-sm text-stone-700 hover:bg-stone-100"
            >
              <span className="truncate">{option}</span>
              <span className="text-xs text-stone-400">
                {t('app:statistics.filters.add', { defaultValue: 'add' })}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SectionTabs({ currentSection, language }) {
  const { t } = useTranslation();
  const languagePrefix = language === 'en' ? '' : `/${language}`;

  return (
    <div className="flex flex-wrap gap-2">
      {STATISTICS_SECTIONS.map((section) => {
        const active = section.key === currentSection;

        return (
          <Link
            key={section.key}
            to={`${languagePrefix}/app/${buildStatisticsRoute(section.key)}`}
            className={cn(
              'cursor-pointer rounded-full border px-3 py-1.5 text-sm transition-colors',
              active
                ? 'border-stone-900 bg-stone-900 text-white'
                : 'border-stone-200 bg-white text-stone-700 hover:border-stone-400'
            )}
          >
            {getSectionLabel(t, section.key)}
          </Link>
        );
      })}
    </div>
  );
}

function SummaryCard({ title, value, subtitle, icon: Icon, accentClass }) {
  return (
    <ShadowBox className="h-full border-stone-200 shadow-sm">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">
            {title}
          </div>
          <div className="mt-3 text-3xl font-semibold tracking-tight text-stone-950">
            {value}
          </div>
          {subtitle && (
            <div className="mt-2 text-sm text-stone-500">{subtitle}</div>
          )}
        </div>

        <div className={cn('rounded-2xl p-3', accentClass)}>
          <Icon className="h-5 w-5" />
        </div>
      </div>
    </ShadowBox>
  );
}

function CustomTooltip({ active, payload, label, metric, metricLabel }) {
  if (!active || !payload?.length) return null;

  return (
    <div className="rounded-2xl border border-stone-200 bg-white px-4 py-3 shadow-lg">
      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">
        {label}
      </div>
      <div className="mt-2 text-sm text-stone-700">
        {metricLabel}: {formatMetricValue(metric, payload[0]?.value)}
      </div>
    </div>
  );
}

function ChartEmptyState({ text, height = 280 }) {
  return (
    <div
      className="flex items-center justify-center rounded-2xl border border-dashed border-stone-200 bg-stone-50 px-4 text-sm text-stone-500"
      style={{ height }}
    >
      {text}
    </div>
  );
}

function TopSignalsList({ rows, metric, summary, title, caption }) {
  const { t } = useTranslation();

  if (!rows.length) {
    return (
      <ChartEmptyState
        text={t('app:statistics.empty.noGroupsCurrentSlice', {
          defaultValue: 'No groups match the current slice.',
        })}
        height={260}
      />
    );
  }

  return (
    <ShadowBox className="h-full border-stone-200 shadow-sm">
      <div className="flex h-full flex-col">
        <div className="mb-5">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">
            {caption}
          </div>
          <h3 className="mt-2 text-xl font-semibold text-stone-950">{title}</h3>
        </div>

        <div className="flex-1 space-y-3">
          {rows.slice(0, 6).map((row, index) => (
            <div key={row.key} className="rounded-2xl bg-stone-50 px-4 py-3">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="text-xs font-semibold uppercase tracking-[0.16em] text-stone-400">
                    #{index + 1}
                  </div>
                  <div className="mt-1 truncate text-base font-medium text-stone-950">
                    {row.label}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-base font-semibold text-stone-950">
                    {formatMetricValue(metric, row[METRIC_CONFIG[metric].dataKey])}
                  </div>
                  <div className="mt-1 text-xs text-stone-500">
                    {formatPercent(getMetricShare(row, summary, metric))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </ShadowBox>
  );
}

function BreakdownTable({ rows, metric, summary }) {
  const { t } = useTranslation();

  if (!rows.length) {
    return (
      <ChartEmptyState
        text={t('app:statistics.empty.noGroupedData', {
          defaultValue: 'No grouped data matches the current filters.',
        })}
        height={260}
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full border-separate border-spacing-y-2">
        <thead>
          <tr className="text-left text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">
            <th className="pb-2">{t('app:statistics.table.group', { defaultValue: 'Group' })}</th>
            <th className="pb-2">{t('app:statistics.table.deals', { defaultValue: 'Deals' })}</th>
            <th className="pb-2">{t('app:statistics.table.capital', { defaultValue: 'Capital' })}</th>
            <th className="pb-2">{t('app:statistics.table.investors', { defaultValue: 'Investors' })}</th>
            <th className="pb-2">{t('app:statistics.table.share', { defaultValue: 'Share' })}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.key} className="rounded-2xl bg-stone-50 text-sm text-stone-800">
              <td className="rounded-l-2xl px-4 py-3 font-medium text-stone-950">
                {row.label}
              </td>
              <td className="px-4 py-3">{compactNumber(row.dealsCount)}</td>
              <td className="px-4 py-3">{formatCurrency(row.totalAmount)}</td>
              <td className="px-4 py-3">{compactNumber(row.activeInvestors)}</td>
              <td className="rounded-r-2xl px-4 py-3">
                {formatPercent(getMetricShare(row, summary, metric))}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TimeSeriesPanel({ analytics, metric, loading }) {
  const { t } = useTranslation();
  const metricConfig = getMetricConfig(t)[metric];
  const hasData = (analytics?.series || []).length > 0;

  return (
    <div className="relative h-full">
      <ShadowBox className="h-full border-stone-200 shadow-sm">
        <div className="flex h-full flex-col">
          <div className="mb-5">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">
              {t('app:statistics.timeSeries.caption', { defaultValue: 'Market pace' })}
            </div>
            <h2 className="mt-2 text-xl font-semibold text-stone-950">
              {t('app:statistics.timeSeries.title', {
                metric: metricConfig.label,
                defaultValue: '{{metric}} over time',
              })}
            </h2>
          </div>

          {hasData ? (
            <div className="h-[340px] flex-1 min-h-[340px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={analytics.series} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id="statisticsArea" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor={metricConfig.color} stopOpacity={0.35} />
                      <stop offset="95%" stopColor={metricConfig.color} stopOpacity={0.02} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#e7e5e4" strokeDasharray="3 3" />
                  <XAxis
                    dataKey="label"
                    tick={{ fill: '#78716c', fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tickFormatter={(value) => formatMetricValue(metric, value)}
                    tick={{ fill: '#78716c', fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    width={80}
                  />
                  <Tooltip content={<CustomTooltip metric={metric} metricLabel={metricConfig.label} />} />
                  <Area
                    type="monotone"
                    dataKey={metricConfig.dataKey}
                    stroke={metricConfig.color}
                    fill="url(#statisticsArea)"
                    strokeWidth={3}
                    activeDot={{ r: 6 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <ChartEmptyState
              text={t('app:statistics.empty.noTimelineData', {
                defaultValue: 'No timeline data matches the current filters.',
              })}
              height={340}
            />
          )}
        </div>
      </ShadowBox>
      {loading && (
        <LoadingOverlay
          text={t('app:statistics.loading.trend', { defaultValue: 'Recomputing trend line...' })}
        />
      )}
    </div>
  );
}

function BreakdownChartPanel({
  title,
  caption,
  rows,
  metric,
  loading,
}) {
  const { t } = useTranslation();
  const metricConfig = getMetricConfig(t)[metric];

  return (
    <div className="relative h-full">
      <ShadowBox className="h-full border-stone-200 shadow-sm">
        <div className="flex h-full flex-col">
          <div className="mb-5">
            <div className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">
              {caption}
            </div>
            <h2 className="mt-2 text-xl font-semibold text-stone-950">{title}</h2>
          </div>

          {rows.length > 0 ? (
            <div className="h-[320px] flex-1 min-h-[320px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={[...rows].reverse()}
                  layout="vertical"
                  margin={{ top: 10, right: 12, left: 12, bottom: 0 }}
                >
                  <CartesianGrid stroke="#f5f5f4" strokeDasharray="3 3" horizontal={false} />
                  <XAxis
                    type="number"
                    tickFormatter={(value) => formatMetricValue(metric, value)}
                    tick={{ fill: '#78716c', fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    type="category"
                    dataKey="label"
                    tick={{ fill: '#44403c', fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    width={120}
                  />
                  <Tooltip content={<CustomTooltip metric={metric} metricLabel={metricConfig.label} />} />
                  <Bar
                    dataKey={metricConfig.dataKey}
                    radius={[0, 10, 10, 0]}
                    fill={metricConfig.color}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <ChartEmptyState
              text={t('app:statistics.empty.noGroupedData', {
                defaultValue: 'No grouped data matches the current filters.',
              })}
              height={320}
            />
          )}
        </div>
      </ShadowBox>
      {loading && (
        <LoadingOverlay
          text={t('app:statistics.loading.breakdown', { defaultValue: 'Refreshing breakdown...' })}
        />
      )}
    </div>
  );
}

function ViewContextCard({
  filters,
  activeFilterCount,
}) {
  const { t } = useTranslation();

  return (
    <ShadowBox className="h-full border-stone-200 shadow-sm">
      <div className="flex h-full flex-col">
        <div className="mb-5">
          <div className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">
            {t('app:statistics.viewContext.caption', { defaultValue: 'Current view' })}
          </div>
          <h3 className="mt-2 text-xl font-semibold text-stone-950">
            {t('app:statistics.viewContext.title', { defaultValue: 'Active analysis settings' })}
          </h3>
        </div>

        <div className="grid flex-1 auto-rows-fr gap-3 sm:grid-cols-2">
          <div className="flex h-full flex-col rounded-2xl bg-stone-50 px-4 py-3">
            <div className="text-xs uppercase tracking-[0.16em] text-stone-500">
              {t('app:statistics.viewContext.metric', { defaultValue: 'Metric' })}
            </div>
            <div className="mt-1 text-base font-medium text-stone-950">
              {getOptionLabel(METRIC_OPTIONS, filters.metric, t)}
            </div>
          </div>
          <div className="flex h-full flex-col rounded-2xl bg-stone-50 px-4 py-3">
            <div className="text-xs uppercase tracking-[0.16em] text-stone-500">
              {t('app:statistics.viewContext.groupBy', { defaultValue: 'Grouped by' })}
            </div>
            <div className="mt-1 text-base font-medium text-stone-950">
              {getOptionLabel(GROUP_BY_OPTIONS, filters.groupBy, t)}
            </div>
          </div>
          <div className="flex h-full flex-col rounded-2xl bg-stone-50 px-4 py-3">
            <div className="text-xs uppercase tracking-[0.16em] text-stone-500">
              {t('app:statistics.viewContext.timeline', { defaultValue: 'Timeline' })}
            </div>
            <div className="mt-1 text-base font-medium text-stone-950">
              {getOptionLabel(INTERVAL_OPTIONS, filters.interval, t)}
            </div>
          </div>
          <div className="flex h-full flex-col rounded-2xl bg-stone-50 px-4 py-3">
            <div className="text-xs uppercase tracking-[0.16em] text-stone-500">
              {t('app:statistics.viewContext.investorScope', { defaultValue: 'Investor scope' })}
            </div>
            <div className="mt-1 text-base font-medium text-stone-950">
              {getOptionLabel(INVESTOR_SCOPE_OPTIONS, filters.investorScope, t)}
            </div>
          </div>
        </div>

        <div className="mt-4 rounded-2xl border border-stone-200 bg-white px-4 py-3 text-sm text-stone-600">
          {t('app:statistics.viewContext.activeFilters', {
            count: activeFilterCount,
            defaultValue: 'Active filters: {{count}}',
          })}
        </div>
      </div>
    </ShadowBox>
  );
}

const Statistics = () => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const [filtersMeta, setFiltersMeta] = useState(null);
  const [filters, setFilters] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [filtersLoading, setFiltersLoading] = useState(true);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [filtersError, setFiltersError] = useState('');
  const [analyticsError, setAnalyticsError] = useState('');
  const metricConfigMap = useMemo(() => getMetricConfig(t), [t]);
  const metricOptions = useMemo(
    () => METRIC_OPTIONS.map((option) => ({ ...option, label: translateLabel(t, option) })),
    [t]
  );
  const groupByOptions = useMemo(
    () => GROUP_BY_OPTIONS.map((option) => ({ ...option, label: translateLabel(t, option) })),
    [t]
  );
  const intervalOptions = useMemo(
    () => INTERVAL_OPTIONS.map((option) => ({ ...option, label: translateLabel(t, option) })),
    [t]
  );
  const investorScopeOptions = useMemo(
    () => INVESTOR_SCOPE_OPTIONS.map((option) => ({ ...option, label: translateLabel(t, option) })),
    [t]
  );

  const currentPath = useMemo(() => {
    let nextPath = location.pathname;

    if (i18n.language !== 'en') {
      nextPath = nextPath.replace(`/${i18n.language}`, '');
    }

    return nextPath
      .replace(/^\/+/, '')
      .replace(/^app\//, '')
      .replace(/\/+$/, '');
  }, [i18n.language, location.pathname]);

  const activeSection = getStatisticsSectionFromPath(currentPath);
  const amountInputTitle = t('app:statistics.filters.amountFormatTooltip', {
    defaultValue: 'Accepted formats: 10k, 2m, 1.3k, 23.00m, 4b.',
  });

  const updateFilters = (recipe) => {
    startTransition(() => {
      setFilters((current) => (current ? recipe(current) : current));
    });
  };

  useEffect(() => {
    let cancelled = false;

    const loadFilters = async () => {
      setFiltersLoading(true);
      setFiltersError('');

      try {
        const data = await fetchStatisticsFilters();
        if (cancelled) return;

        setFiltersMeta(data);
        setFilters(createDefaultFilters(data));
      } catch (error) {
        if (cancelled) return;
        setFiltersError(
          error?.response?.data?.message
          || t('app:statistics.errors.filters', { defaultValue: 'Failed to load statistics filters.' })
        );
      } finally {
        if (!cancelled) {
          setFiltersLoading(false);
        }
      }
    };

    loadFilters();

    return () => {
      cancelled = true;
    };
  }, [t]);

  useEffect(() => {
    if (!filters) return undefined;

    let cancelled = false;
    const timer = setTimeout(async () => {
      setAnalyticsLoading(true);
      setAnalyticsError('');

      try {
        const data = await fetchStatisticsMarketTrends({
          dateFrom: formatDateForApi(filters.dateFrom),
          dateTo: formatDateForApi(filters.dateTo),
          industries: filters.industries,
          rounds: filters.rounds,
          investorCountries: filters.investorCountries,
          investorTypes: filters.investorTypes,
          amountMin: filters.amountMin,
          amountMax: filters.amountMax,
          metric: filters.metric,
          groupBy: filters.groupBy,
          interval: filters.interval,
          investorScope: filters.investorScope,
          limit: 12,
        });

        if (cancelled) return;
        setAnalytics(data);
      } catch (error) {
        if (cancelled) return;
        setAnalyticsError(
          error?.response?.data?.message
          || t('app:statistics.errors.analytics', { defaultValue: 'Failed to load market trends.' })
        );
      } finally {
        if (!cancelled) {
          setAnalyticsLoading(false);
        }
      }
    }, 180);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [filters, t]);

  const metric = filters?.metric || 'amount';
  const metricConfig = metricConfigMap[metric];

  const summary = analytics?.summary || {
    dealsCount: 0,
    totalAmount: 0,
    averageAmount: 0,
    activeInvestors: 0,
    activeCountries: 0,
    activeIndustries: 0,
    activeRounds: 0,
  };

  const focusData = analytics?.table || [];
  const secondaryGroupKey = useMemo(() => {
    const candidates = ['industry', 'country', 'investorType', 'round'].filter(
      (key) => key !== (filters?.groupBy || 'industry')
    );

    return candidates.find((key) => (analytics?.breakdowns?.[key] || []).length > 0) || candidates[0] || 'country';
  }, [analytics?.breakdowns, filters?.groupBy]);

  const secondaryData = analytics?.breakdowns?.[secondaryGroupKey] || [];

  const latestPoint = useMemo(() => {
    const series = analytics?.series || [];
    return series.length > 0 ? series[series.length - 1] : null;
  }, [analytics?.series]);

  const peakPoint = useMemo(() => {
    const series = analytics?.series || [];
    if (!series.length) return null;

    return series.reduce((best, current) => (
      current[metricConfig.dataKey] > best[metricConfig.dataKey] ? current : best
    ));
  }, [analytics?.series, metricConfig.dataKey]);

  const activeFilterCount = useMemo(() => {
    if (!filters) return 0;

    return [
      filters.industries.length > 0,
      filters.rounds.length > 0,
      filters.investorCountries.length > 0,
      filters.investorTypes.length > 0,
      filters.amountMin != null,
      filters.amountMax != null,
      filters.datePreset === 'custom',
    ].filter(Boolean).length;
  }, [filters]);

  if (filtersLoading) {
    return (
      <div className="py-10 text-sm text-stone-500">
        {t('app:statistics.loading.workspace', { defaultValue: 'Loading statistics workspace...' })}
      </div>
    );
  }

  if (filtersError) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
        {filtersError}
      </div>
    );
  }

  const renderOverview = () => (
    <div className="space-y-7 pt-3">
      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <SummaryCard
          title={t('app:statistics.overview.matchedDeals', { defaultValue: 'Matched deals' })}
          value={compactNumber(summary.dealsCount)}
          subtitle={t('app:statistics.overview.activeRounds', {
            count: compactNumber(summary.activeRounds),
            defaultValue: '{{count}} active rounds',
          })}
          icon={ChartColumnIncreasing}
          accentClass="bg-blue-50 text-blue-700"
        />
        <SummaryCard
          title={t('app:statistics.overview.capitalDeployed', { defaultValue: 'Capital deployed' })}
          value={formatCurrency(summary.totalAmount)}
          subtitle={t('app:statistics.overview.avgTicket', {
            value: formatCurrency(summary.averageAmount),
            defaultValue: 'Avg ticket {{value}}',
          })}
          icon={ChartLine}
          accentClass="bg-emerald-50 text-emerald-700"
        />
        <SummaryCard
          title={t('app:statistics.overview.activeInvestors', { defaultValue: 'Active investors' })}
          value={compactNumber(summary.activeInvestors)}
          subtitle={t('app:statistics.overview.countriesRepresented', {
            count: compactNumber(summary.activeCountries),
            defaultValue: '{{count}} countries represented',
          })}
          icon={Globe2}
          accentClass="bg-amber-50 text-amber-700"
        />
        <SummaryCard
          title={t('app:statistics.overview.trackedIndustries', { defaultValue: 'Tracked industries' })}
          value={compactNumber(summary.activeIndustries)}
          subtitle={filters?.investorScope === 'lead'
            ? t('app:statistics.scopes.leadSummary', { defaultValue: 'Lead investors only' })
            : t('app:statistics.scopes.allSummary', { defaultValue: 'All participating investors' })}
          icon={Layers3}
          accentClass="bg-stone-100 text-stone-700"
        />
      </div>

      <div className="grid items-stretch gap-6 xl:grid-cols-[minmax(0,1.4fr)_minmax(0,0.9fr)]">
        <TimeSeriesPanel analytics={analytics} metric={metric} loading={analyticsLoading} />
        <TopSignalsList
          rows={focusData}
          metric={metric}
          summary={summary}
          title={t('app:statistics.overview.topSignalsTitle', {
            group: getOptionLabel(GROUP_BY_OPTIONS, filters.groupBy, t),
            defaultValue: '{{group}} focus',
          })}
          caption={t('app:statistics.overview.topSignalsCaption', { defaultValue: 'Snapshot' })}
        />
      </div>
    </div>
  );

  const renderTrends = () => (
    <div className="space-y-6 pt-3">
      <div className="grid items-stretch gap-6 xl:grid-cols-[minmax(0,1.45fr)_minmax(0,0.85fr)]">
        <TimeSeriesPanel analytics={analytics} metric={metric} loading={analyticsLoading} />
        <div className="relative h-full">
          <ShadowBox className="h-full border-stone-200 shadow-sm">
            <div className="flex h-full flex-col">
              <div className="mb-5">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">
                  {t('app:statistics.trends.caption', { defaultValue: 'Trend insights' })}
                </div>
                <h2 className="mt-2 text-xl font-semibold text-stone-950">
                  {t('app:statistics.trends.title', { defaultValue: 'What the current curve says' })}
                </h2>
              </div>

              <div className="flex flex-1 flex-col gap-4">
                <div className="rounded-2xl bg-stone-50 px-4 py-4">
                  <div className="text-xs uppercase tracking-[0.16em] text-stone-500">
                    {t('app:statistics.trends.latest', { defaultValue: 'Latest point' })}
                  </div>
                  <div className="mt-1 text-lg font-semibold text-stone-950">
                    {latestPoint
                      ? `${latestPoint.label}: ${formatMetricValue(metric, latestPoint[metricConfig.dataKey])}`
                      : t('app:statistics.empty.noData', { defaultValue: 'No data' })}
                  </div>
                </div>

                <div className="rounded-2xl bg-stone-50 px-4 py-4">
                  <div className="text-xs uppercase tracking-[0.16em] text-stone-500">
                    {t('app:statistics.trends.peak', { defaultValue: 'Peak point' })}
                  </div>
                  <div className="mt-1 text-lg font-semibold text-stone-950">
                    {peakPoint
                      ? `${peakPoint.label}: ${formatMetricValue(metric, peakPoint[metricConfig.dataKey])}`
                      : t('app:statistics.empty.noData', { defaultValue: 'No data' })}
                  </div>
                </div>

                <div className="rounded-2xl border border-stone-200 bg-white px-4 py-4 text-sm leading-6 text-stone-600">
                  {t('app:statistics.trends.helper', {
                    defaultValue: 'Use this page when you want to answer whether the market is heating up or cooling down for the current slice, before drilling into categories.',
                  })}
                </div>
              </div>
            </div>
          </ShadowBox>
          {analyticsLoading && (
            <LoadingOverlay
              text={t('app:statistics.loading.insights', { defaultValue: 'Refreshing trend insights...' })}
            />
          )}
        </div>
      </div>
    </div>
  );

  const renderBreakdowns = () => (
    <div className="space-y-6 pt-3">
      <div className="grid items-stretch gap-6 xl:grid-cols-2">
        <BreakdownChartPanel
          title={t('app:statistics.breakdowns.title', {
            group: getOptionLabel(GROUP_BY_OPTIONS, filters.groupBy, t),
            defaultValue: '{{group}} view',
          })}
          caption={t('app:statistics.breakdowns.primaryCaption', { defaultValue: 'Primary breakdown' })}
          rows={focusData}
          metric={metric}
          loading={analyticsLoading}
        />
        <BreakdownChartPanel
          title={t('app:statistics.breakdowns.title', {
            group: getOptionLabel(GROUP_BY_OPTIONS, secondaryGroupKey, t),
            defaultValue: '{{group}} view',
          })}
          caption={t('app:statistics.breakdowns.secondaryCaption', { defaultValue: 'Secondary breakdown' })}
          rows={secondaryData}
          metric={metric}
          loading={analyticsLoading}
        />
      </div>
    </div>
  );

  const renderRankings = () => (
    <div className="space-y-6 pt-3">
      <div className="grid items-stretch gap-6 xl:grid-cols-[minmax(0,1.15fr)_minmax(0,0.85fr)]">
        <div className="relative h-full">
          <ShadowBox className="h-full border-stone-200 shadow-sm">
            <div className="flex h-full flex-col">
              <div className="mb-5">
                <div className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">
                  {t('app:statistics.rankings.caption', { defaultValue: 'Ranking table' })}
                </div>
                <h2 className="mt-2 text-xl font-semibold text-stone-950">
                  {t('app:statistics.rankings.title', {
                    group: getOptionLabel(GROUP_BY_OPTIONS, filters.groupBy, t),
                    defaultValue: '{{group}} ranking',
                  })}
                </h2>
              </div>
              <div className="flex-1">
                <BreakdownTable rows={focusData} metric={metric} summary={summary} />
              </div>
            </div>
          </ShadowBox>
          {analyticsLoading && (
            <LoadingOverlay
              text={t('app:statistics.loading.ranking', { defaultValue: 'Refreshing ranking table...' })}
            />
          )}
        </div>

        <TopSignalsList
          rows={focusData}
          metric={metric}
          summary={summary}
          title={t('app:statistics.rankings.shortlistTitle', { defaultValue: 'Fast shortlist' })}
          caption={t('app:statistics.rankings.shortlistCaption', { defaultValue: 'Highest signal' })}
        />
      </div>
    </div>
  );

  return (
    <div className="space-y-10 pb-12 xl:space-y-12" aria-busy={analyticsLoading ? 'true' : 'false'}>
      <section className="flex flex-col gap-4 rounded-[28px] border border-stone-200 bg-gradient-to-br from-white via-stone-50 to-emerald-50 px-5 py-5 shadow-sm">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div className="max-w-3xl">
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-emerald-700">
              {t('app:statistics.kicker', { defaultValue: 'Professional analytics' })}
            </div>
            <h1 className="mt-2 text-2xl font-semibold tracking-tight text-stone-950 sm:text-[2rem]">
              {t('app:statistics.title', { defaultValue: 'Market trends and investor activity' })}
            </h1>
            <p className="mt-2 max-w-2xl text-sm leading-5 text-stone-600">
              {t('app:statistics.subtitle', {
                defaultValue: 'Filter the deal flow by period, industry, round, investor geography and investor type to see where capital is actually moving.',
              })}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2 xl:justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => filtersMeta && setFilters(createDefaultFilters(filtersMeta))}
              iconLeft={<RefreshCw className="h-4 w-4" />}
            >
              {t('app:statistics.resetView', { defaultValue: 'Reset view' })}
            </Button>
          </div>
        </div>

        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <SectionTabs currentSection={activeSection} language={i18n.language} />

          <div className="flex flex-wrap gap-2">
            {QUICK_PRESETS.map((preset) => {
              const active = filters?.datePreset === preset.key;

              return (
                <button
                  key={preset.key}
                  type="button"
                onClick={() => {
                  const nextPreset = applyPreset(preset.key, filtersMeta);
                  updateFilters((current) => ({
                    ...current,
                    ...nextPreset,
                  }));
                }}
                className={cn(
                  'cursor-pointer rounded-full border px-3.5 py-1.5 text-sm transition-colors',
                  active
                    ? 'border-stone-900 bg-stone-900 text-white'
                    : 'border-stone-200 bg-white text-stone-700 hover:border-stone-400'
                )}
                >
                  {translateLabel(t, preset)}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      <ShadowBox className="border-stone-200 shadow-sm">
        <div className="grid gap-4 xl:grid-cols-[minmax(0,0.96fr)_minmax(0,1.04fr)]">
          <div className="grid gap-3 md:grid-cols-2">
            <MultiSelectFilter
              label={t('app:statistics.filters.industriesLabel', { defaultValue: 'Industry categories' })}
              placeholder={t('app:statistics.filters.industriesPlaceholder', { defaultValue: 'Filter startup categories' })}
              options={filtersMeta?.industries || []}
              values={filters?.industries || []}
              onChange={(industries) => updateFilters((current) => ({ ...current, industries }))}
            />
            <MultiSelectFilter
              label={t('app:statistics.filters.countriesLabel', { defaultValue: 'Investor countries' })}
              placeholder={t('app:statistics.filters.countriesPlaceholder', { defaultValue: 'Filter investor geography' })}
              options={filtersMeta?.investorCountries || []}
              values={filters?.investorCountries || []}
              onChange={(investorCountries) => updateFilters((current) => ({ ...current, investorCountries }))}
            />
            <MultiSelectFilter
              label={t('app:statistics.filters.typesLabel', { defaultValue: 'Investor types' })}
              placeholder={t('app:statistics.filters.typesPlaceholder', { defaultValue: 'Filter by investor profile' })}
              options={filtersMeta?.investorTypes || []}
              values={filters?.investorTypes || []}
              onChange={(investorTypes) => updateFilters((current) => ({ ...current, investorTypes }))}
            />
            <MultiSelectFilter
              label={t('app:statistics.filters.roundsLabel', { defaultValue: 'Rounds' })}
              placeholder={t('app:statistics.filters.roundsPlaceholder', { defaultValue: 'Filter by round' })}
              options={filtersMeta?.rounds || []}
              values={filters?.rounds || []}
              onChange={(rounds) => updateFilters((current) => ({ ...current, rounds }))}
            />
          </div>

          <div className="flex h-full flex-col rounded-[22px] border border-stone-200 bg-stone-50/70 px-3.5 py-3 shadow-sm">
            <div className="grid gap-3 sm:grid-cols-2">
              <div>
                <div className="mb-1.5 flex items-center gap-2 text-sm font-medium text-stone-800">
                  <span>{t('app:statistics.filters.amountFromLabel', { defaultValue: 'Amount from' })}</span>
                  <HoverHint message={amountInputTitle}>
                    <span className="inline-flex h-5 w-5 cursor-help items-center justify-center rounded-full border border-stone-200 bg-white text-[11px] text-stone-500">
                      ?
                    </span>
                  </HoverHint>
                </div>
                <InputNumber
                  value={filters?.amountMin}
                  onChange={(amountMin) => updateFilters((current) => ({ ...current, amountMin }))}
                  placeholder={t('app:statistics.filters.amountFromPlaceholder', { defaultValue: 'e.g. 500k' })}
                  title={amountInputTitle}
                />
              </div>
              <div>
                <div className="mb-1.5 flex items-center gap-2 text-sm font-medium text-stone-800">
                  <span>{t('app:statistics.filters.amountToLabel', { defaultValue: 'Amount to' })}</span>
                  <HoverHint message={amountInputTitle}>
                    <span className="inline-flex h-5 w-5 cursor-help items-center justify-center rounded-full border border-stone-200 bg-white text-[11px] text-stone-500">
                      ?
                    </span>
                  </HoverHint>
                </div>
                <InputNumber
                  value={filters?.amountMax}
                  onChange={(amountMax) => updateFilters((current) => ({ ...current, amountMax }))}
                  placeholder={t('app:statistics.filters.amountToPlaceholder', { defaultValue: 'e.g. 25m' })}
                  title={amountInputTitle}
                />
              </div>
            </div>

            <div className="mt-4 grid gap-3 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
              <div>
                <div className="mb-1.5 text-sm font-medium text-stone-800">
                  {t('app:statistics.filters.customPeriodLabel', { defaultValue: 'Custom period' })}
                </div>
                <div className="flex flex-wrap items-center gap-2.5">
                  <DatePicker
                    selectedDate={filters?.dateFrom}
                    onDateChange={(dateFrom) => updateFilters((current) => ({
                      ...current,
                      datePreset: 'custom',
                      dateFrom: dateFrom ? startOfDay(dateFrom) : current.dateFrom,
                    }))}
                    placeholder={t('app:statistics.filters.fromPlaceholder', { defaultValue: 'From' })}
                  />
                  <ArrowRightLeft className="h-4 w-4 text-stone-400" />
                  <DatePicker
                    selectedDate={filters?.dateTo}
                    onDateChange={(dateTo) => updateFilters((current) => ({
                      ...current,
                      datePreset: 'custom',
                      dateTo: dateTo ? endOfDay(dateTo) : current.dateTo,
                    }))}
                    placeholder={t('app:statistics.filters.toPlaceholder', { defaultValue: 'To' })}
                  />
                </div>
              </div>

              <div className="flex items-end lg:h-full">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full lg:w-auto"
                  onClick={() => updateFilters((current) => ({
                    ...current,
                    amountMin: null,
                    amountMax: null,
                    industries: [],
                    rounds: [],
                    investorCountries: [],
                    investorTypes: [],
                  }))}
                >
                  {t('app:statistics.filters.clearFilters', { defaultValue: 'Clear filters' })}
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-6 border-t border-stone-200/80 pt-4">
          <div className="grid items-stretch gap-4 xl:grid-cols-4">
            <SegmentedControl
              label={t('app:statistics.controls.metric', { defaultValue: 'Metric' })}
              options={metricOptions}
              value={filters?.metric || 'amount'}
              onChange={(selectedMetric) => updateFilters((current) => ({ ...current, metric: selectedMetric }))}
            />
            <SegmentedControl
              label={t('app:statistics.controls.groupBy', { defaultValue: 'Group by' })}
              options={groupByOptions}
              value={filters?.groupBy || 'industry'}
              onChange={(groupBy) => updateFilters((current) => ({ ...current, groupBy }))}
            />
            <SegmentedControl
              label={t('app:statistics.controls.timeline', { defaultValue: 'Timeline' })}
              options={intervalOptions}
              value={filters?.interval || 'month'}
              onChange={(interval) => updateFilters((current) => ({ ...current, interval }))}
            />
            <SegmentedControl
              label={t('app:statistics.controls.investorScope', { defaultValue: 'Investor scope' })}
              options={investorScopeOptions}
              value={filters?.investorScope || 'all'}
              onChange={(investorScope) => updateFilters((current) => ({ ...current, investorScope }))}
            />
          </div>
        </div>
      </ShadowBox>

      {analyticsLoading && (
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-5 py-4 text-sm text-amber-900 shadow-sm">
          <div className="flex items-start gap-3">
            <RefreshCw className="mt-0.5 h-4 w-4 animate-spin flex-none" />
            <div>
              <div className="font-medium">
                {t('app:statistics.loading.bannerTitle', {
                  defaultValue: 'Recomputing this slice of the market.',
                })}
              </div>
              <div className="mt-1 text-amber-800/90">
                {t('app:statistics.loading.bannerBody', {
                  defaultValue: 'Keeping the previous results on screen while the new grouping loads. Bigger combinations can take a few seconds.',
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {analyticsError && (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700">
          {analyticsError}
        </div>
      )}

      <div className="grid items-stretch gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,0.9fr)]">
        <ViewContextCard filters={filters} activeFilterCount={activeFilterCount} />

        <ShadowBox className="h-full border-stone-200 shadow-sm">
          <div className="flex h-full flex-col">
            <div className="mb-5">
              <div className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">
                {t('app:statistics.workspace.caption', { defaultValue: 'Workspace section' })}
              </div>
              <h2 className="mt-2 text-xl font-semibold text-stone-950">
                {getSectionLabel(t, activeSection)}
              </h2>
            </div>

            <div className="grid flex-1 auto-rows-fr gap-3 sm:grid-cols-3">
              <div className="flex h-full flex-col rounded-2xl bg-stone-50 px-4 py-3">
                <div className="flex items-center gap-2 text-sm font-medium text-stone-900">
                  <Clock3 className="h-4 w-4 text-stone-500" />
                  {t('app:statistics.workspace.trendsTitle', { defaultValue: 'Trends' })}
                </div>
                <p className="mt-2 text-sm leading-6 text-stone-600">
                  {t('app:statistics.workspace.trendsBody', {
                    defaultValue: 'Best for timing the market and spotting acceleration or slowdown.',
                  })}
                </p>
              </div>
              <div className="flex h-full flex-col rounded-2xl bg-stone-50 px-4 py-3">
                <div className="flex items-center gap-2 text-sm font-medium text-stone-900">
                  <Layers3 className="h-4 w-4 text-stone-500" />
                  {t('app:statistics.workspace.breakdownsTitle', { defaultValue: 'Breakdowns' })}
                </div>
                <p className="mt-2 text-sm leading-6 text-stone-600">
                  {t('app:statistics.workspace.breakdownsBody', {
                    defaultValue: 'Best for seeing which industries, countries or investor types dominate.',
                  })}
                </p>
              </div>
              <div className="flex h-full flex-col rounded-2xl bg-stone-50 px-4 py-3">
                <div className="flex items-center gap-2 text-sm font-medium text-stone-900">
                  <Rows3 className="h-4 w-4 text-stone-500" />
                  {t('app:statistics.workspace.rankingsTitle', { defaultValue: 'Rankings' })}
                </div>
                <p className="mt-2 text-sm leading-6 text-stone-600">
                  {t('app:statistics.workspace.rankingsBody', {
                    defaultValue: 'Best for direct comparison and quick export-oriented review.',
                  })}
                </p>
              </div>
            </div>
          </div>
        </ShadowBox>
      </div>

      <Routes>
        <Route index element={<Navigate to="overview" replace />} />
        <Route path="overview" element={renderOverview()} />
        <Route path="trends" element={renderTrends()} />
        <Route path="breakdowns" element={renderBreakdowns()} />
        <Route path="rankings" element={renderRankings()} />
        <Route path="*" element={<Navigate to="overview" replace />} />
      </Routes>

      <div className="text-xs leading-5 text-stone-500">
        {t('app:statistics.footerNote', {
          defaultValue: 'Grouped views count a deal once per group it belongs to, so grouped capital can exceed the headline total when one deal spans multiple industries, countries or investor types.',
        })}
      </div>
    </div>
  );
};

export default Statistics;
